import {
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
  useRef,
  useState,
} from "react";

import {
  Box,
  Column,
  DragIcon,
  Menu,
  MenuActionsButton,
  MenuList,
  PlusIcon,
  Row,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { DeleteButton } from "src/components/delete-button";
import type { AmpTaskRowFragment } from "src/graphql";
import { isPendingId, reorderPositions } from "src/pages/projects/board-cache";
import { useBoard } from "src/pages/projects/board-context";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import { useBoardErrorToast } from "src/pages/projects/hooks";
import { taskPeekTargetProps } from "src/pages/projects/task-peek-keys";

import { TaskLink } from "./board-links";
import { DropLine } from "./drop-line";
import {
  dropIndex,
  type SortDrop,
  useDraggableItem,
  useDropIndicator,
  useGroupDropTarget,
} from "./sortable-dnd";
import {
  AssigneesPicker,
  DueDatePicker,
  StatusPicker,
} from "./task-properties";

const SUBTASK_KIND = "subtask";

type Props = {
  parent: Pick<AmpTaskRowFragment, "id" | "project_id">;
  subtasks: AmpTaskRowFragment[];
  /** Lets the page's Add button move focus into the field that adds one. */
  addInputRef?: RefObject<HTMLInputElement>;
  /** Shows the add field among existing subtasks. A list with none always has it. */
  isAdding: boolean;
  onIsAddingChange: (isAdding: boolean) => void;
};

/** A task's subtasks: a checklist of lighter tasks, ordered by hand. */
export const Subtasks = ({
  parent,
  subtasks,
  addInputRef,
  isAdding,
  onIsAddingChange,
}: Props) => {
  const board = useBoard();
  const listRef = useRef<HTMLDivElement>(null);
  const ordered = [...subtasks].sort((a, b) => a.position - b.position);

  const drop = (target: SortDrop) => {
    const moved = ordered.find((task) => task.id === target.itemId);
    if (!moved) return;
    const index = dropIndex(
      ordered.map((task) => task.id),
      target,
    );
    board.tasks.reorder(reorderPositions(ordered, moved, index));
  };
  useGroupDropTarget(listRef, SUBTASK_KIND, drop, ordered.length > 1);

  return (
    <Column
      ref={listRef}
      bg="white"
      borderWidth="hairline"
      borderColor="base.border"
      borderRadius="2xl"
      overflow="hidden"
    >
      {ordered.map((task) => (
        <SubtaskRow key={task.id} task={task} />
      ))}
      {(ordered.length === 0 || isAdding) && (
        <AddSubtaskRow
          parent={parent}
          inputRef={addInputRef}
          autoFocus={isAdding}
          onDone={() => onIsAddingChange(false)}
        />
      )}
    </Column>
  );
};

/** How many of a task's subtasks are done, as a bar and a count. */
export const SubtaskProgress = ({
  done,
  total,
}: {
  done: number;
  total: number;
}) => (
  <Row align="center" gap={3}>
    <Box
      flex={1}
      maxWidth="2xs"
      height={1.5}
      borderRadius="full"
      bg="base.divider"
      overflow="hidden"
    >
      <Box
        height="100%"
        width={`${(done / total) * 100}%`}
        bg="success.base"
        borderRadius="full"
      />
    </Box>
    <Text size="sm" color="text.tertiary">
      {`${done} of ${total} done`}
    </Text>
  </Row>
);

const SubtaskRow = ({ task }: { task: AmpTaskRowFragment }) => {
  const board = useBoard();
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const isPending = isPendingId(task.id);
  const isDragging = useDraggableItem(ref, SUBTASK_KIND, task.id, {
    isEnabled: !isPending,
    handleRef,
  });
  const edge = useDropIndicator(ref, SUBTASK_KIND, task.id, !isPending);

  return (
    <Row
      ref={ref}
      {...taskPeekTargetProps(task.id)}
      className="group"
      position="relative"
      align="center"
      gap={2}
      pl={2}
      pr={2}
      py={2}
      borderBottomWidth="hairline"
      borderColor="base.divider"
      opacity={isDragging ? 0.4 : 1}
      // Until the server returns it, a new subtask has nothing to write to.
      pointerEvents={isPending ? "none" : undefined}
      aria-busy={isPending || undefined}
    >
      {edge && <DropLine edge={edge} />}
      <Box
        ref={handleRef}
        aria-hidden
        // In the row's left padding, so the status icon sits where the add row's icon does.
        position="absolute"
        left={0}
        top="50%"
        transform="translateY(-50%)"
        display="flex"
        color="text.tertiary"
        cursor="grab"
        opacity={0}
        _groupHover={{ opacity: 1 }}
      >
        <Box as={DragIcon} boxSize={3} />
      </Box>
      <StatusPicker task={task} appearance="ghost" iconOnly />
      <TaskLink
        taskId={task.id}
        flex={1}
        minWidth={0}
        borderRadius="sm"
        _focusVisible={{ boxShadow: "outline", outline: "none" }}
        _hover={{ textDecoration: "underline" }}
      >
        <TextWithTooltip>{task.title}</TextWithTooltip>
      </TaskLink>
      <Row width={24} flexShrink={0} justify="flex-end">
        <DueDatePicker task={task} appearance="ghost" hideWhenEmpty />
      </Row>
      <AssigneesPicker task={task} appearance="ghost" avatarsOnly />
      {/* The removal confirmation lives in the menu, so the menu must stay mounted after it closes. */}
      <Menu isLazy={false}>
        <MenuActionsButton size="sm" />
        <MenuList>
          <DeleteButton
            variant="menu"
            label={TERMS.subtask.singular}
            action="remove"
            onDelete={() => board.tasks.removeSubtask(task.id)}
          />
        </MenuList>
      </Menu>
    </Row>
  );
};

/** Adds subtasks one after another: Enter saves and keeps the field for the next. */
const AddSubtaskRow = ({
  parent,
  inputRef,
  autoFocus,
  onDone,
}: {
  parent: Pick<AmpTaskRowFragment, "id" | "project_id">;
  inputRef?: RefObject<HTMLInputElement>;
  autoFocus: boolean;
  /** Called when the field is left empty or dismissed. */
  onDone: () => void;
}) => {
  const board = useBoard();
  const showError = useBoardErrorToast();
  const [title, setTitle] = useState("");

  const add = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setTitle("");
    board.tasks
      .create({
        projectId: parent.project_id,
        parent: { kind: "task", taskId: parent.id },
        title: trimmed,
      })
      .catch((error: unknown) => {
        // Puts the title back for another try, unless the next one is already being typed.
        setTitle((current) => current || trimmed);
        showError(`Couldn't add the ${TERMS.subtask.singular}`, error);
      });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter also confirms an IME composition, which isn't a submit.
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      add();
    } else if (event.key === "Escape") {
      setTitle("");
      onDone();
    }
  };

  return (
    <Row align="center" gap={2} px={3} py={3}>
      <Box as={PlusIcon} boxSize={4} color="text.tertiary" flexShrink={0} />
      <Box
        ref={inputRef}
        as="input"
        aria-label={`Add ${TERMS.subtask.withArticle}`}
        flex={1}
        minWidth={0}
        bg="transparent"
        border="none"
        outline="none"
        borderRadius="sm"
        _focusVisible={{ boxShadow: "outline" }}
        placeholder={`Add ${TERMS.subtask.withArticle}…`}
        _placeholder={{ color: "text.tertiary" }}
        value={title}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setTitle(event.target.value)
        }
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (!title.trim()) onDone();
        }}
        autoFocus={autoFocus}
      />
    </Row>
  );
};
